import type { Email } from '@compass/client/types/email';
import { Button } from '@components/ui/button';
import { Copy } from 'lucide-react';
import React from 'react';

import { FormattedDateTime } from '@/components/FormattedDateTime';
import { getEmailStatusDisplayText } from '@/utils/callsTabs/statusDisplay';
import { copyToClipboard } from '@/utils/clipboard';

import type { PopoverContentProps, TabLabelContext, TabTypeHandler } from './types';

const getLabel = (_data: Email, _context: TabLabelContext): string => {
  return 'Email';
};

const EmailPopoverContent: React.FC<PopoverContentProps<Email>> = ({ data: email }) => {
  return (
    <div className="space-y-2 text-sm">
      <div className="flex justify-between gap-8 items-center">
        <span className="text-muted-foreground">Email ID:</span>
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs">{email.id}</span>
          {email.id && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => copyToClipboard(email.id!)}
              className="h-6 w-6 p-0"
            >
              <Copy className="w-3 h-3" />
            </Button>
          )}
        </div>
      </div>
      <div className="flex justify-between gap-8">
        <span className="text-muted-foreground">Type:</span>
        <span>Email</span>
      </div>
      <div className="flex justify-between gap-8">
        <span className="text-muted-foreground">Status:</span>
        <span>{getEmailStatusDisplayText(email.status)}</span>
      </div>
      {email.to_addresses && email.to_addresses.length > 0 && (
        <div className="flex justify-between gap-8">
          <span className="text-muted-foreground">To:</span>
          <span>{email.to_addresses.join(', ')}</span>
        </div>
      )}
      {email.subject && (
        <div className="flex justify-between gap-8">
          <span className="text-muted-foreground">Subject:</span>
          <span className="max-w-[250px] truncate">{email.subject}</span>
        </div>
      )}
      {email.body && (
        <div className="">
          <div className="bg-secondary p-2 rounded text-xs">
            <div className="text-muted-foreground mb-1">Body:</div>
            <div className="text-foreground whitespace-pre-wrap max-w-[400px] max-h-20 overflow-auto">
              {email.body}
            </div>
          </div>
        </div>
      )}
      {email.error && (
        <div className="flex justify-between gap-8">
          <span className="text-muted-foreground">Error:</span>
          <span className="text-destructive">{email.error}</span>
        </div>
      )}
      {email.created_at && (
        <div className="flex justify-between gap-8">
          <span className="text-muted-foreground">Created:</span>
          <FormattedDateTime value={email.created_at} />
        </div>
      )}
      {email.delivered_at && (
        <div className="flex justify-between gap-8">
          <span className="text-muted-foreground">Delivered:</span>
          <FormattedDateTime value={email.delivered_at} />
        </div>
      )}
    </div>
  );
};

export const emailHandler: TabTypeHandler<Email> = {
  isDisabled: true,
  getLabel,
  getStatusLabel: data => getEmailStatusDisplayText(data.status),
  PopoverContent: EmailPopoverContent,
};
